import React, { useState, useEffect } from 'react';
import { adminService } from '../services/api';
import './SimpleDashboard.css';

const PERIODS = [
  { id: 'hoy', label: 'Hoy' },
  { id: '7dias', label: '7 días' },
  { id: '30dias', label: '30 días' },
  { id: 'mes', label: 'Este mes' }
];

/** Orden lógico operativo (clave BD → etiqueta en español) */
const ORDER_STATUS_PIPELINE = [
  ['pending', 'Pendiente'],
  ['awaiting_packaging', 'Empaque pendiente'],
  ['ready_to_ship', 'Cola empaque (legacy)'],
  ['processing', 'Listo para guía'],
  ['shipped', 'Enviado'],
  ['delivered', 'Entregado'],
  ['cancelled', 'Cancelado']
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const SimpleDashboard = ({ operationsSummary = null }) => {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('7dias');

  useEffect(() => {
    const fetchKpis = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminService.getKpis(period);
        setKpis(data);
      } catch (err) {
        console.error('Error cargando KPIs:', err);
        setError(err.response?.status === 401 
          ? 'Sesión expirada. Inicia sesión de nuevo.' 
          : 'Error al cargar los datos del dashboard.');
      } finally {
        setLoading(false);
      }
    };

    fetchKpis();
  }, [period]);

  if (loading && !kpis) {
    return (
      <div className="simple-dashboard">
        <div className="dashboard-header">
          <h1>Dashboard Administrativo</h1>
          <p>Cargando datos en vivo...</p>
        </div>
        <div className="dashboard-loading">
          <i className="fas fa-spinner fa-spin fa-3x"></i>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="simple-dashboard">
        <div className="dashboard-header">
          <h1>Dashboard Administrativo</h1>
        </div>
        <div className="dashboard-error">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const { ventas, pedidos, ticket_promedio, productos_activos, ordenes_pendientes, comparativa } = kpis || {};
  const ventasVar = comparativa?.ventas_variacion ?? 0;
  const pedidosVar = comparativa?.pedidos_variacion ?? 0;

  const byStatus = operationsSummary?.orders?.by_status || {};
  const queues = operationsSummary?.orders?.queues || {};
  const attention = operationsSummary?.orders?.attention_total;
  const payOpen = operationsSummary?.orders?.payment_pending_open;
  const pendingInvoices = operationsSummary?.invoices_pending_review ?? operationsSummary?.badges?.invoices;
  const pendingTestimonials =
    operationsSummary?.testimonials_pending ?? operationsSummary?.badges?.testimonials;

  return (
    <div className="simple-dashboard">
      <div className="dashboard-header">
        <h1>Dashboard Administrativo</h1>
        <p>Datos en vivo desde la base de datos</p>
        <div className="period-selector">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              className={`period-btn ${period === p.id ? 'active' : ''}`}
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="dashboard-content">
        {operationsSummary && (
          <section className="dashboard-ops-section" aria-label="Resumen operativo de pedidos">
            <h2 className="dashboard-ops-title">Pedidos y colas (operación)</h2>
            <p className="dashboard-ops-intro">
              Conteos en vivo: qué requiere acción en Empaque, guía y pagos abiertos. Los números rojos en el menú
              coinciden con estas colas.
            </p>
            <div className="dashboard-ops-cards">
              <div className="dashboard-ops-pill">
                <span className="dashboard-ops-pill-label">Atención total (órdenes)</span>
                <strong>{attention ?? '—'}</strong>
              </div>
              <div className="dashboard-ops-pill">
                <span className="dashboard-ops-pill-label">Cola empaque</span>
                <strong>{queues.packaging ?? '—'}</strong>
              </div>
              <div className="dashboard-ops-pill">
                <span className="dashboard-ops-pill-label">Listos para guía</span>
                <strong>{queues.shipping_need_guide ?? '—'}</strong>
              </div>
              <div className="dashboard-ops-pill dashboard-ops-pill--muted">
                <span className="dashboard-ops-pill-label">Pagos pending / failed (no canceladas)</span>
                <strong>{payOpen ?? '—'}</strong>
              </div>
              <div className="dashboard-ops-pill dashboard-ops-pill--muted">
                <span className="dashboard-ops-pill-label">Facturas en revisión</span>
                <strong>{pendingInvoices ?? 0}</strong>
              </div>
              <div className="dashboard-ops-pill dashboard-ops-pill--muted">
                <span className="dashboard-ops-pill-label">Testimonios pendientes</span>
                <strong>{pendingTestimonials ?? 0}</strong>
              </div>
            </div>
            <div className="dashboard-ops-table-wrap">
              <table className="dashboard-ops-table">
                <caption className="visually-hidden">Cantidad de órdenes por estado en base de datos</caption>
                <thead>
                  <tr>
                    <th scope="col">Estado (BD)</th>
                    <th scope="col">Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {ORDER_STATUS_PIPELINE.map(([key, label]) => (
                    <tr key={key}>
                      <td>
                        <code className="dashboard-ops-code">{key}</code>
                        <span className="dashboard-ops-label"> {label}</span>
                      </td>
                      <td>{byStatus[key] ?? 0}</td>
                    </tr>
                  ))}
                  {Object.keys(byStatus)
                    .filter((k) => !ORDER_STATUS_PIPELINE.some(([key]) => key === k))
                    .map((k) => (
                      <tr key={`extra-${k}`}>
                        <td>
                          <code className="dashboard-ops-code">{k}</code>
                        </td>
                        <td>{byStatus[k]}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <div className="dashboard-grid">
          <div className="dashboard-card">
            <div className="card-icon sales">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <div className="card-content">
              <h3>Ventas</h3>
              <p className="card-number">{formatCurrency(ventas)}</p>
              <p className={`card-change ${ventasVar >= 0 ? 'positive' : 'negative'}`}>
                {ventasVar >= 0 ? '+' : ''}{ventasVar}% vs período anterior
              </p>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-icon orders">
              <i className="fas fa-shopping-cart"></i>
            </div>
            <div className="card-content">
              <h3>Pedidos</h3>
              <p className="card-number">{pedidos ?? 0}</p>
              <p className={`card-change ${pedidosVar >= 0 ? 'positive' : 'negative'}`}>
                {pedidosVar >= 0 ? '+' : ''}{pedidosVar}% vs período anterior
              </p>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-icon ticket">
              <i className="fas fa-receipt"></i>
            </div>
            <div className="card-content">
              <h3>Ticket promedio</h3>
              <p className="card-number">{formatCurrency(ticket_promedio)}</p>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-icon products">
              <i className="fas fa-box"></i>
            </div>
            <div className="card-content">
              <h3>Productos activos</h3>
              <p className="card-number">{productos_activos ?? 0}</p>
            </div>
          </div>

          <div className="dashboard-card">
            <div className="card-icon pending">
              <i className="fas fa-truck"></i>
            </div>
            <div className="card-content">
              <h3>Órdenes pendientes</h3>
              <p className="card-number">{ordenes_pendientes ?? 0}</p>
              <p className="card-subtitle">Por enviar</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleDashboard;
